import * as React from "react"

import { cn } from "lib/utils"

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Grow with the content instead of scrolling (no fixed height). */
  autoResize?: boolean
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, autoResize, value, ...props }, ref) => {
    const inner = React.useRef<HTMLTextAreaElement | null>(null)
    React.useImperativeHandle(ref, () => inner.current as HTMLTextAreaElement)

    React.useLayoutEffect(() => {
      const el = inner.current
      if (!autoResize || !el) return
      const fit = () => {
        el.style.height = "auto"
        el.style.height = `${el.scrollHeight + 2}px` // + top/bottom border
      }
      fit()
      // a width change (window resize, sidebar toggle) changes how the text wraps
      const ro = new ResizeObserver(fit)
      ro.observe(el.parentElement ?? el)
      return () => ro.disconnect()
    }, [autoResize, value])

    return (
      <textarea
        className={cn(
          "flex min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          autoResize && "resize-none overflow-hidden",
          className
        )}
        ref={inner}
        value={value}
        {...props}
      />
    )
  }
)
Textarea.displayName = "Textarea"

export { Textarea }
